import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import {NgForOf, NgIf} from "@angular/common";
import {ControlContainer, FormControl, FormGroupDirective} from "@angular/forms";
import {InfoTooltipComponent} from "../../info-tooltip/info-tooltip.component";
import {Subscription} from "rxjs";

@Component({
  selector: 'app-dropdown-select',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    InfoTooltipComponent
  ],
  templateUrl: './dropdown-select.component.html',
  styleUrl: './dropdown-select.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class DropdownSelectComponent implements OnInit, OnChanges, OnDestroy {

  @Input() label: string = 'Select option';
  @Input() controlName: string = '';
  @Input() options: Record<string, any>[] = [];
  @Input() required: boolean = false;
  @Input() showIcon: boolean = false;
  @Input() placeholder: string = 'Select an option';
  @Input() selectedOption: Record<string, any> | null = null;
  @Output() optionSelected = new EventEmitter<Record<string, any>>();

  @Input() tooltip: string = '';
  @Input() tooltipPosition: string = 'right';
  @Input() tooltipClass: string = 'custom-tooltip';
  @Input() showlabel: boolean = true;
  isOpen = false;
  private clickListener: () => void;
  private valueChangesSubscription: Subscription;


  constructor(
    public controlContainer: FormGroupDirective,
    private elRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.initializeSelectedOption();

    // Subscribe to form control value changes
    if (this.formControl) {
      this.valueChangesSubscription = this.formControl.valueChanges.subscribe(value => {
        // Re-initialize selected option when value changes
        this.initializeSelectedOption();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Re-initialize when options change (first time or subsequent changes)
    if (changes['options']) {
      this.initializeSelectedOption();
    }
  }

  ngOnDestroy() {
    this.removeClickOutsideListener();

    // Clean up value changes subscription
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }

  initializeSelectedOption() {
    if (this.options && this.options.length > 0) {
      const selectedId = this.formControl.value;
      if (selectedId !== null) {
        this.selectedOption = this.options.find(option => option['id'] === selectedId) || null;
      }
    } else {
      console.warn('DropdownSelectComponent: options array is undefined or empty.');
    }
  }

  get formControl(): FormControl {
    return this.controlContainer.control.get(this.controlName) as FormControl;
  }

  toggleDropdown(event: MouseEvent) {
    event.stopPropagation(); // Prevent the click from closing the dropdown immediately
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.addClickOutsideListener();
    } else {
      this.removeClickOutsideListener();
    }
  }

  selectOption(option: Record<string, any>) {
    this.selectedOption = option;
    this.formControl.setValue(option['id']);
    this.isOpen = false;
    this.optionSelected.emit(option);
    this.removeClickOutsideListener();
  }

  private addClickOutsideListener() {
    this.clickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (targetElement && !this.elRef.nativeElement.querySelector('button').contains(targetElement)) {
        this.isOpen = false;
        this.removeClickOutsideListener();
      }
    });
  }

  private removeClickOutsideListener() {
    if (this.clickListener) {
      this.clickListener(); // Unsubscribe the event listener
      this.clickListener = null;
    }
  }

  get hasValue(): boolean {
    return this.selectedOption !== null && this.formControl?.value !== null && this.formControl?.value !== undefined;
  }

}
